import { useEffect } from 'react';
import { usePlatformBranding } from '@/modules/marketing/hooks/use-platform-branding';

export interface PageSeoInput {
    title: string;
    description: string;
    keywords?: string;
}

function setMetaTag(attr: 'name' | 'property', key: string, content: string | null | undefined) {
    if (!content) return;

    let tag = document.querySelector<HTMLMetaElement>(`meta[${attr}="${key}"]`);
    if (!tag) {
        tag = document.createElement('meta');
        tag.setAttribute(attr, key);
        document.head.appendChild(tag);
    }
    tag.content = content;
}

/**
 * Per-page SEO — every marketing page calls this with its own unique
 * title/description/keywords, which is what actually matters for search
 * ranking (one title for the whole site, as System Settings alone produced,
 * gives every page the same signal). The platform name from Super Admin
 * branding is still folded into the title so a rename takes effect
 * everywhere without redeploying. Runtime, not build-time, for the same
 * reason as the favicon in MarketingHeader — index.html ships with no
 * per-page tags to begin with.
 */
export function usePageSeo({ title, description, keywords }: PageSeoInput) {
    const { data: branding } = usePlatformBranding();
    const platformName = branding?.platform_name || 'AIPA';
    const fullTitle = `${title} | ${platformName}`;

    useEffect(() => {
        document.title = fullTitle;

        setMetaTag('name', 'description', description);
        setMetaTag('name', 'keywords', keywords);

        setMetaTag('property', 'og:title', fullTitle);
        setMetaTag('property', 'og:description', description);
        setMetaTag('property', 'og:type', 'website');
        setMetaTag('property', 'og:site_name', platformName);
        setMetaTag('property', 'og:url', window.location.href);

        setMetaTag('name', 'twitter:card', 'summary_large_image');
        setMetaTag('name', 'twitter:title', fullTitle);
        setMetaTag('name', 'twitter:description', description);

        let canonical = document.querySelector<HTMLLinkElement>('link[rel="canonical"]');
        if (!canonical) {
            canonical = document.createElement('link');
            canonical.rel = 'canonical';
            document.head.appendChild(canonical);
        }
        canonical.href = window.location.origin + window.location.pathname;
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [fullTitle, description, keywords, platformName]);
}
